// QUEM VÊ AS SENHAS — a lista de pessoas que podem revelar (2026-09-03).
//
// Pedido do Lucas: "crie ali nessa página uma ferramenta para se cadastrar quem
// pode e quem não pode ver as senhas".
//
// 🚨 Esta tela NÃO é um atalho para o Admin › Permissões — ela É a fonte.
//    O sistema não tem permissão por PESSOA (só pessoa→perfil→permissão), e
//    para "quem vê a senha" perfil é a régua errada: no dia em que o módulo
//    subiu, liberar UMA pessoa obrigou a liberar DOIS PERFIS inteiros, e quem
//    entrasse neles depois herdaria o acesso sem ninguém decidir.
//    Por isso `comercial.portais.revelar` foi aposentada: quem revela é quem
//    está nesta lista, e mais ninguém.
//
// A lista de pessoas vem do Cognito (`GET /admin/usuarios`) — não há tabela de
// usuários no Postgres, e o nome só existe lá.
import { useCallback, useEffect, useState } from 'react';
import {
  App as AntApp, Alert, Empty, Flex, Input, Modal, Space, Switch, Table, Tag, Tooltip, Typography,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { listarAcessoPortais, definirAcessoPortal, AcessoPessoa } from './apiPortais';

const { Text, Paragraph } = Typography;

type Pessoa = { email: string; nome: string };

export default function ModalQuemVeSenhas(
  { aberto, aoFechar, aoMudar }: { aberto: boolean; aoFechar: () => void; aoMudar?: () => void },
) {
  const { message, modal } = AntApp.useApp();
  const [carregando, setCarregando] = useState(true);
  const [pessoas, setPessoas] = useState<Pessoa[] | null>([]);
  // true quando o /admin/usuarios nao respondeu: a tela vira somente-leitura
  // em vez de acusar todo mundo de estar fora do sistema.
  const [cognitoFora, setCognitoFora] = useState(false);
  const [acesso, setAcesso] = useState<AcessoPessoa[]>([]);
  const [podeGerenciar, setPodeGerenciar] = useState(false);
  const [eu, setEu] = useState('');
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [r, us] = await Promise.all([
        listarAcessoPortais(),
        (async () => {
          try {
            const token = await getToken();
            const x = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
            const d = x.data.body ? JSON.parse(x.data.body) : x.data;
            return (Array.isArray(d) ? d : [])
              .filter((u: any) => u.email)
              .map((u: any) => ({ email: String(u.email).toLowerCase(), nome: u.nome || '' }));
          } catch {
            // 🚨 `null`, não `[]`. Com lista vazia, TODA pessoa da lista de
            //    acesso caía no ramo "não está no Cognito" e a tela acusava
            //    todo mundo de "fora do sistema · vale tirar" — um alarme
            //    falso que convida a revogar quem está certo, justamente
            //    quando o problema é a outra Lambda estar fora do ar.
            return null;
          }
        })(),
      ]);
      setAcesso(r.acesso || []);
      setPodeGerenciar(!!r.pode_gerenciar_acesso);
      setEu(r.eu || '');
      setPessoas(us);
      setCognitoFora(us === null);
      if (us === null) {
        message.warning('Não consegui listar as pessoas do sistema agora. '
          + 'Mostro quem já tem acesso, mas sem os nomes e sem poder acrescentar.');
      }
    } catch (e: any) {
      message.error('Não foi possível abrir a lista: ' + (e.response?.data?.error || e.message));
    } finally { setCarregando(false); }
  }, [message]);

  useEffect(() => { if (aberto) carregar(); }, [aberto, carregar]);

  // Une as duas fontes: as pessoas do Cognito + quem está na lista de acesso e
  // já não está no Cognito (saiu da empresa e ninguém revogou — é exatamente
  // quem se quer enxergar aqui).
  const porEmail = new Map<string, AcessoPessoa>();
  acesso.forEach((a) => porEmail.set(a.usuario_email.toLowerCase(), a));
  const listaPessoas = pessoas || [];
  const linhas = [
    ...listaPessoas.map((p) => ({ ...p, reg: porEmail.get(p.email), fantasma: false })),
    ...acesso
      .filter((a) => !listaPessoas.some((p) => p.email === a.usuario_email.toLowerCase()))
      .map((a) => ({
        email: a.usuario_email.toLowerCase(),
        nome: a.nome_snap || a.usuario_email.split('@')[0],
        // So e 'fantasma' se a lista do Cognito REALMENTE carregou.
        reg: a, fantasma: !cognitoFora,
      })),
  ]
    .filter((l) => !busca
      || l.email.includes(busca.toLowerCase())
      || (l.nome || '').toLowerCase().includes(busca.toLowerCase()))
    .sort((a, b) => {
      const av = a.reg?.ativo ? 0 : 1, bv = b.reg?.ativo ? 0 : 1;
      return av - bv || (a.nome || a.email).localeCompare(b.nome || b.email);
    });

  const ativos = acesso.filter((a) => a.ativo).length;

  async function alternar(linha: any, ligar: boolean) {
    const acao = ligar ? 'passa a ver' : 'deixa de ver';
    const quem = linha.nome || linha.email;
    const ok = await modal.confirm({
      title: ligar ? `Liberar ${quem}?` : `Tirar o acesso de ${quem}?`,
      content: ligar
        ? `${quem} ${acao} as senhas de todos os portais. Cada vez que revelar, fica registrado com nome e hora.`
        : `${quem} ${acao} as senhas. Vale imediatamente — não há espera.`,
      okText: ligar ? 'Liberar' : 'Tirar acesso',
      okButtonProps: ligar ? undefined : { danger: true },
      cancelText: 'Cancelar',
    });
    if (!ok) return;
    setSalvando(linha.email);
    try {
      const r = await definirAcessoPortal(linha.email, ligar, linha.nome);
      // Não afirmar o que não aconteceu: o servidor devolve `ja_estava` /
      // `ja_estava_fora` quando o clique não mudou nada (dois cliques rápidos,
      // duas abas). Dizer "já consegue ver" nesses casos é verdade por acaso.
      const semMudanca = (r as any)?.ja_estava || (r as any)?.ja_estava_fora;
      if (semMudanca) message.info(`${quem} já estava ${ligar ? 'liberado' : 'sem acesso'}.`);
      else message.success(ligar ? `${quem} já consegue ver as senhas.` : `${quem} não vê mais as senhas.`);
      // Se a pessoa mexeu no PRÓPRIO acesso, a tela de trás precisa saber:
      // sem isto ela continua com o botão Revelar desenhado (ou sem ele).
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setSalvando(null); }
  }

  return (
    <Modal
      open={aberto} onCancel={aoFechar} footer={null}
      title="Quem vê as senhas dos portais"
      width="94vw" style={{ maxWidth: 820, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' } }}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Esta lista é quem decide — não o Admin › Permissões"
        description={
          <Paragraph style={{ marginBottom: 0, fontSize: 12 }}>
            Quem está ligado aqui consegue revelar a senha de qualquer portal, e cada
            revelação fica registrada com nome e hora. Quem está desligado vê o cadastro
            (portal, login, situação), mas não a senha. Mudança vale <b>na hora</b>.
          </Paragraph>
        }
      />

      {!podeGerenciar && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Você está vendo quem tem acesso, mas não pode alterar."
          description="Quem muda esta lista é quem tem a permissão de definir acesso aos portais."
        />
      )}

      <Flex justify="space-between" align="center" gap={8} wrap style={{ marginBottom: 10 }}>
        <Input.Search
          allowClear placeholder="Buscar pessoa" style={{ maxWidth: 280 }}
          onSearch={setBusca} onChange={(e) => !e.target.value && setBusca('')}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {ativos} {ativos === 1 ? 'pessoa vê' : 'pessoas veem'} as senhas
        </Text>
      </Flex>

      <Table
        rowKey="email" size="small" loading={carregando} dataSource={linhas}
        pagination={false} tableLayout="fixed"
        locale={{ emptyText: <Empty description="Ninguém encontrado" /> }}
        columns={[
          {
            title: 'Pessoa', dataIndex: 'nome', width: '46%',
            render: (_: any, l: any) => (
              <Space direction="vertical" size={0}>
                <Space size={6}>
                  <Text strong={!!l.reg?.ativo}>{l.nome || l.email}</Text>
                  {l.email === eu && <Tag color="blue">você</Tag>}
                  {l.fantasma && (
                    <Tooltip title="Está na lista de acesso mas não aparece mais no sistema. Vale tirar.">
                      <Tag color="red">fora do sistema</Tag>
                    </Tooltip>
                  )}
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>{l.email}</Text>
              </Space>
            ),
          },
          {
            title: 'Vê as senhas', width: 120, align: 'center' as const,
            render: (_: any, l: any) => (
              <Switch
                checked={!!l.reg?.ativo}
                disabled={!podeGerenciar || cognitoFora || salvando === l.email}
                loading={salvando === l.email}
                onChange={(v) => alternar(l, v)}
              />
            ),
          },
          {
            title: 'Desde / até quando', width: '32%',
            render: (_: any, l: any) => {
              const r = l.reg;
              if (!r) return <Text type="secondary" style={{ fontSize: 11 }}>nunca teve</Text>;
              return (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {r.ativo
                    ? `desde ${dayjs(r.concedido_em).format('DD/MM/YY')} · por ${(r.concedido_por || '').split('@')[0]}`
                    : `tirado em ${r.revogado_em ? dayjs(r.revogado_em).format('DD/MM/YY') : '—'}`
                      + ` · por ${(r.revogado_por || '').split('@')[0]}`}
                </Text>
              );
            },
          },
        ] as any}
      />
    </Modal>
  );
}
